import React, { useState, useEffect } from 'react';

interface Team {
  TeamId: string;
  TeamName: string;
  DepartmentId: string;
  TeamLeadEmail: string;
}

interface TeamWithMetrics extends Team {
  memberCount: number;
  capacityAllocated: number;
  capacityTotal: number;
}

export default function DepartmentLeadHomepage() {
  const [teams, setTeams] = useState<TeamWithMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);

      // Get user's department
      const authEmail = localStorage.getItem('auth_email') || 'deptlead@example.com';
      const userRes = await fetch(`/users/me?email=${encodeURIComponent(authEmail)}`);
      const userData = await userRes.json();
      const userDepartmentId = userData.data?.DepartmentId;

      // Fetch real team capacity metrics for all teams in department
      const capacityUrl = userDepartmentId
        ? `/team-capacity?departmentId=${encodeURIComponent(userDepartmentId)}`
        : '/team-capacity';

      const capacityRes = await fetch(capacityUrl);
      const capacityData = await capacityRes.json();

      if (capacityData.success && capacityData.data) {
        const teamsWithMetrics: TeamWithMetrics[] = capacityData.data.map((metric: any) => ({
          TeamId: metric.TeamId,
          TeamName: metric.TeamName,
          DepartmentId: userDepartmentId || '',
          TeamLeadEmail: '', // Not returned from capacity endpoint, can be fetched separately if needed
          memberCount: metric.TotalMembers,
          capacityAllocated: metric.LoggedHours,
          capacityTotal: metric.TotalCapacityHours,
        }));

        setTeams(teamsWithMetrics);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const getCapacityColor = (allocated: number, total: number) => {
    const percentage = (allocated / total) * 100;
    if (percentage > 100) return '#d32f2f'; // red
    if (percentage >= 80) return '#f57c00'; // yellow
    return '#388e3c'; // green
  };

  const getCapacityPercentage = (allocated: number, total: number) => {
    return Math.round((allocated / total) * 100);
  };

  const totalMembers = teams.reduce((sum, team) => sum + team.memberCount, 0);
  const totalCapacityAllocated = teams.reduce((sum, team) => sum + team.capacityAllocated, 0);
  const totalCapacityAvailable = teams.reduce((sum, team) => sum + team.capacityTotal, 0);
  const avgUtilization = totalCapacityAvailable > 0
    ? Math.round((totalCapacityAllocated / totalCapacityAvailable) * 100)
    : 0;

  return (
    <div style={{ padding: '0 20px' }}>
      {error && (
        <div style={{ background: '#ffebee', padding: '1rem', borderRadius: '4px', marginBottom: '1rem', color: '#c62828' }}>
          {error}
        </div>
      )}

      {/* Summary Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Teams</p>
          <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2rem' }}>{teams.length}</h2>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Total Members</p>
          <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2rem' }}>{totalMembers}</h2>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Capacity Used</p>
          <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2rem' }}>{totalCapacityAllocated}/{totalCapacityAvailable} hrs</h2>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Avg Utilization</p>
          <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2rem' }}>{avgUtilization}%</h2>
        </div>
      </div>

      {/* Teams Grid */}
      <div style={{ marginBottom: '2rem' }}>
        <h2>Teams</h2>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading teams...</div>
        ) : teams.length === 0 ? (
          <div style={{ padding: '2rem', background: '#f9f9f9', borderRadius: '4px', textAlign: 'center', color: '#666' }}>
            No teams found
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {teams.map(team => {
              const capacityPercentage = getCapacityPercentage(team.capacityAllocated, team.capacityTotal);
              const capacityColor = getCapacityColor(team.capacityAllocated, team.capacityTotal);

              return (
                <div key={team.TeamId} style={{ background: 'white', padding: '1.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0' }}>{team.TeamName}</h3>
                  <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                    <strong>Lead:</strong> {team.TeamLeadEmail}
                  </p>

                  <div style={{ margin: '1rem 0 0 0', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                    <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                      <strong>Members:</strong> {team.memberCount}
                    </p>

                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.9rem', color: '#666' }}>Capacity</span>
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: capacityColor }}>
                          {capacityPercentage}%
                        </span>
                      </div>
                      <div style={{ background: '#eee', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                        <div
                          style={{
                            background: capacityColor,
                            height: '100%',
                            width: `${Math.min(capacityPercentage, 100)}%`,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <p style={{ margin: '0.5rem 0 0 0', color: '#999', fontSize: '0.85rem' }}>
                        {team.capacityAllocated}/{team.capacityTotal} hours allocated
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
